import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, DollarSign, FileText, AlertTriangle, ChevronRight, ArrowLeft, TrendingUp } from 'lucide-react';

const C = { navy: '#0D1B2A', saffron: '#FF9933', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', blue: '#1A4C7C' };
function fmt$(v) { if (!v) return '$0'; if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${v.toLocaleString()}`; }

export default function BrokerScorecardPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('gwp');
  const [sortDir, setSortDir] = useState(-1);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/analytics/brokers').then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  const selectBroker = async (name) => {
    setSelectedBroker(name);
    const r = await api.get(`/analytics/brokers/${encodeURIComponent(name)}`);
    setDetail(r.data);
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;
  if (!data) return null;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(-1); }
  };
  const sorted = [...data.brokers].sort((a, b) => ((a[sortKey] ?? -1) - (b[sortKey] ?? -1)) * sortDir);
  const MEDALS = ['#C9A227', '#8E9BAA', '#B07A4B'];

  return (
    <div className="space-y-5" data-testid="broker-scorecard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>Broker Scorecard</h1>
          {selectedBroker ? (
            <div className="flex items-center gap-1.5 text-sm mt-1">
              <button onClick={() => { setSelectedBroker(null); setDetail(null); }} className="hover:underline" style={{ color: C.blue }}>All Brokers</button>
              <ChevronRight className="w-3.5 h-3.5" style={{ color: C.slate }} />
              <span style={{ color: C.navy }} className="font-medium">{selectedBroker}</span>
            </div>
          ) : (
            <p className="text-sm mt-1" style={{ color: C.slate }}>{data.total_brokers} brokers — {fmt$(data.total_gwp)} total GWP</p>
          )}
        </div>
        {selectedBroker && (
          <button onClick={() => { setSelectedBroker(null); setDetail(null); }} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium hover:bg-[rgba(26,76,124,0.08)]" style={{ color: C.blue }}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
      </div>

      {!selectedBroker ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Total Brokers" value={data.total_brokers} color={C.blue} />
            <KPI label="Total GWP" value={fmt$(data.total_gwp)} color={C.saffron} />
            <KPI label="Total Policies" value={data.total_policies.toLocaleString()} color={C.green} />
            <KPI label="Panel Hit Rate" value={`${data.panel_hit_rate ?? 0}%`} color={(data.panel_hit_rate ?? 0) >= 40 ? C.green : C.amber} />
            <KPI label="Panel Loss Ratio" value={`${data.panel_loss_ratio ?? 0}%`} color={(data.panel_loss_ratio ?? 0) > 60 ? C.red : (data.panel_loss_ratio ?? 0) > 40 ? C.amber : C.green} />
          </div>

          <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="p-3 border-b border-[rgba(136,150,168,0.15)] flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Broker Leaderboard</h3>
              <span className="text-[10px]" style={{ color: C.slate }}>Click a column to re-rank</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="broker-table">
                <thead>
                  <tr style={{ background: C.bg }} className="border-b border-[rgba(136,150,168,0.15)]">
                    <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>#</th>
                    <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>Broker</th>
                    {[
                      ['policy_count', 'Policies'], ['gwp', 'GWP'], ['gwp_share', 'Share'],
                      ['submissions', 'Subs'], ['hit_rate', 'Hit Rate'],
                      ['claims_count', 'Claims'], ['loss_ratio_est', 'Loss Ratio'],
                    ].map(([key, label]) => (
                      <th key={key} onClick={() => toggleSort(key)} data-testid={`sort-${key}`}
                        className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-medium cursor-pointer select-none hover:text-[#1A4C7C]"
                        style={{ color: sortKey === key ? C.blue : C.slate }}>
                        {label}{sortKey === key ? (sortDir === -1 ? ' ▼' : ' ▲') : ''}
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((b, i) => (
                    <tr key={b.broker_name} onClick={() => selectBroker(b.broker_name)}
                      className="border-b border-[rgba(136,150,168,0.08)] hover:bg-[#F5F7FA] cursor-pointer group" data-testid={`broker-row-${i}`}>
                      <td className="py-2 px-2 text-xs font-bold" style={{ color: i < 3 ? MEDALS[i] : C.slate }}>{i + 1}</td>
                      <td className="py-2 px-2"><div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" style={{ color: i < 3 ? MEDALS[i] : C.blue }} /><span className="text-xs font-medium" style={{ color: C.navy }}>{b.broker_name}</span></div></td>
                      <td className="py-2 px-2 text-xs font-mono" style={{ color: C.navy }}>{b.policy_count}</td>
                      <td className="py-2 px-2 text-xs font-mono font-medium" style={{ color: C.navy }}>{fmt$(b.gwp)}</td>
                      <td className="py-2 px-2"><div className="flex items-center gap-1"><div className="w-12 h-1.5 rounded-full" style={{ background: C.bg }}><div className="h-1.5 rounded-full" style={{ width: `${Math.min(b.gwp_share * 3, 100)}%`, background: C.blue }} /></div><span className="text-[10px]" style={{ color: C.slate }}>{b.gwp_share}%</span></div></td>
                      <td className="py-2 px-2 text-xs font-mono" style={{ color: C.slate }}>{b.submissions}</td>
                      <td className="py-2 px-2 text-xs font-mono" data-testid={`hit-rate-${i}`} style={{ color: b.hit_rate == null ? C.slate : b.hit_rate >= 40 ? C.green : b.hit_rate >= 20 ? C.amber : C.red }}>{b.hit_rate == null ? '—' : `${b.hit_rate}%`}</td>
                      <td className="py-2 px-2 text-xs" style={{ color: b.claims_count > 0 ? C.amber : C.green }}>{b.claims_count}</td>
                      <td className="py-2 px-2 text-xs font-mono" style={{ color: b.loss_ratio_est > 60 ? C.red : b.loss_ratio_est > 40 ? C.amber : C.green }}>{b.loss_ratio_est}%</td>
                      <td className="py-1 px-1"><ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: C.blue }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : detail && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Policies" value={detail.policy_count} color={C.blue} />
            <KPI label="GWP" value={fmt$(detail.gwp)} color={C.saffron} />
            <KPI label="Avg Premium" value={fmt$(detail.avg_premium)} color={C.navy} />
            <KPI label="Avg Score" value={detail.avg_score} color={C.teal} />
            <KPI label="Retention" value={`${detail.renewal_retention}%`} color={detail.renewal_retention >= 80 ? C.green : C.amber} />
          </div>

          <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Class Breakdown</h3>
            <div className="space-y-2">
              {detail.by_class.map(c => (
                <div key={c.class} className="flex items-center justify-between py-1.5 border-b border-[rgba(136,150,168,0.08)]">
                  <span className="text-xs font-medium" style={{ color: C.navy }}>{c.class}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs" style={{ color: C.slate }}>{c.count} policies</span>
                    <span className="text-xs font-mono font-medium" style={{ color: C.navy }}>{fmt$(c.gwp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {detail.recent_policies?.length > 0 && (
            <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
              <div className="p-3 border-b border-[rgba(136,150,168,0.15)]">
                <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Recent Policies</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr style={{ background: C.bg }}>
                    {['Policy #', 'Business', 'Class', 'Premium', 'Status'].map(h => (
                      <th key={h} className="text-left py-1.5 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {detail.recent_policies.map(p => (
                      <tr key={p.policy_id} className="border-t border-[rgba(136,150,168,0.08)]">
                        <td className="py-1.5 px-3 font-mono" style={{ color: C.blue }}>{p.policy_number}</td>
                        <td className="py-1.5 px-3 font-medium truncate max-w-[180px]" style={{ color: C.navy }}>{p.business_name}</td>
                        <td className="py-1.5 px-3" style={{ color: C.slate }}>{p.class}</td>
                        <td className="py-1.5 px-3 font-mono" style={{ color: C.navy }}>${p.annual_premium?.toLocaleString()}</td>
                        <td className="py-1.5 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium capitalize" style={{ background: (p.status === 'active' ? C.green : C.slate) + '15', color: p.status === 'active' ? C.green : C.slate }}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5" style={{ color: color || C.navy }}>{value}</p>
    </Card>
  );
}
